
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";

import PanelHeader from "@/components/Reusable/Util/PanelHeader.vue";
import TabsRow from "@/components/Reusable/Tabs/TabsRow.vue";
import Tab from "@/components/Reusable/Tabs/Tab.vue";
import GlobalVariablesInput from "@/oxygen/variables/GlobalVariablesInput.vue";
import SearchInput from "@/components/Reusable/CustomInputs/SearchInput.vue";

import { globalStore, uiStore } from "@/store/store";
import { closeUiPanel } from "@/lib/panels/panels";
import { ClosingConfirmationChosenOption } from "@/types/ui";
import {
  refetchVariablesFromServerToGlobalStore,
  saveVariablesToWordPress
} from "@/oxygen/variables/lib/data";
import PanelHeaderButton from "@/components/Reusable/Util/PanelHeaderButton.vue";
import ManageVariables from "@/oxygen/variables/panel/ManageVariables.vue";
import VariablesHistory from "@/oxygen/variables/panel/VariablesHistory.vue";
import IconButton from "@/components/UI/IconButton.vue";
import PanelMask from "@/oxygen/PanelMask.vue";
import ImportExportDropdownMenu from "@/components/Settings/DocumentOrGlobalSettings/ImportExportDropdownMenu.vue";
import NotificationLogger from "@/lib/notifications/notifications";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import { Variable, VariableListV } from "@/oxygen/variables/lib/types";
import { breakdanceUndo } from "@/lib/undo/undo";
import { DEFAULT_NEW_COLLECTION } from "@/oxygen/classes/lib/classes";
import { mergeVariables } from "@/oxygen/variables/lib/importer";

@Component({
  components: {
    ImportExportDropdownMenu,
    PanelMask,
    IconButton,
    Tab,
    TabsRow,
    PanelHeader,
    SearchInput,
    ManageVariables,
    VariablesHistory,
    PanelHeaderButton,
    GlobalVariablesInput
  }
})
export default class VariablesPanel extends Vue {
  isLoadingData = false;
  isStoringData = false;

  get settingsToExport() {
    return globalStore.variables;
  }

  get viewMode() {
    return uiStore.panelState.variablesPanelPanelActiveViewMode;
  }

  async beforeMount() {
    if (uiStore.panelState.shouldLoadFreshDataIntoStoreOnceOpened.variables) {
      await this.load();
    } else {
      uiStore.setShouldPanelLoadFreshDataIntoStoreOnceOpened("variables", true);
    }
  }

  toggleViewMode() {
    uiStore.setPanelState({
      ...uiStore.panelState,
      variablesPanelPanelActiveViewMode:
        this.viewMode === "controls" ? "history" : "controls"
    });
  }

  async load() {
    this.isLoadingData = true;
    const isSuccessful = await refetchVariablesFromServerToGlobalStore();
    this.isLoadingData = false;

    return isSuccessful;
  }

  async save() {
    this.isStoringData = true;
    const isSuccessful = await saveVariablesToWordPress();
    this.isStoringData = false;

    return isSuccessful;
  }

  requestPanelClosingConfirmation() {
    uiStore.requestClosingConfirmation({
      subject: "variables",
      confirmationDefinition: {
        title: "You have unsaved changes to your Variables.",
        text: "Would you like to save or discard these changes?",
        type: "info"
      }
    });
  }

  get unsavedChangesPresent(): boolean {
    return globalStore.variablesHasUnsavedChangesPresent;
  }

  @Watch("unsavedChangesPresent", { immediate: true })
  handleUnsavedChangesPresentChange(unsavedChangesPresent: boolean) {
    if (unsavedChangesPresent) {
      this.requestPanelClosingConfirmation();
    } else {
      uiStore.noClosingConfirmationIsNeededFor("variables");
    }
  }

  get confirmationChosenOption(): ClosingConfirmationChosenOption | undefined {
    return uiStore.closingConfirmations.variables?.chosenOption;
  }

  @Watch("confirmationChosenOption", { immediate: true })
  async handleConfirmationChosenOptionChange(
    chosenOption: ClosingConfirmationChosenOption | undefined
  ) {
    if (chosenOption === "discard") {
      uiStore.setClosingConfirmationIsDiscarding({
        subject: "variables",
        isDiscarding: true
      });
      if (await this.load()) {
        this.close();
      } else {
        this.requestPanelClosingConfirmation();
      }
    } else if (chosenOption === "save") {
      uiStore.setClosingConfirmationIsSaving({
        subject: "variables",
        isSaving: true
      });
      if (await this.save()) {
        this.close();
      } else {
        this.requestPanelClosingConfirmation();
      }
    }
  }

  get isLoading() {
    return this.isLoadingData || this.isStoringData;
  }

  close() {
    closeUiPanel("variables");
  }

  addCollection() {
    if (this.viewMode == "history") this.toggleViewMode();
    const newCollection = DEFAULT_NEW_COLLECTION;
    const existingCollections = globalStore.variablesCollections;

    let count = 1;
    while (existingCollections.includes(`${newCollection} ${count}`)) {
      count++;
    }

    const finalCollectionName = `${newCollection} ${count}`;

    globalStore.setVariablesCollections([
      ...existingCollections,
      finalCollectionName
    ]);
  }

  validateImport(settingsToImport: Record<string, unknown>) {
    if (!Array.isArray(settingsToImport)) {
      NotificationLogger.log.errorMessage(
        "Error importing Variables - the file is invalid or empty."
      );
      return false;
    }

    return pipe(
      settingsToImport,
      VariableListV.decode,
      fold(
        () => {
          NotificationLogger.log.errorMessage(
            "Error importing Variables - the file is invalid."
          );
          return false;
        },
        () => {
          return true;
        }
      )
    );
  }

  importVariables(variablesToImport: Variable[], mode: "merge" | "replace") {
    if (!Array.isArray(variablesToImport)) {
      NotificationLogger.log.errorMessage("No Variables to import.");
      return;
    }

    breakdanceUndo.transaction(() => {
      if (mode === "replace") {
        globalStore.setVariables(variablesToImport);
      } else {
        // Merge and overwrite existing variables by cssVariableName

        const { variablesToKeep, variables } = mergeVariables(
          globalStore.variables,
          variablesToImport
        );

        globalStore.setVariables(variables);

        NotificationLogger.log.message(
          `${variablesToImport.length} variables imported. ${variablesToKeep.length} existing variables were kept.`
        );
      }
    }, "Import Variables");
  }
}
